import { Component, DestroyRef, inject, InjectionToken, NgZone, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { UserService } from 'src/app/core/services/user.service';
import { isTransientProfileError } from 'src/app/core/services/user.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@auth0/auth0-angular';
import { AppInsightsService } from 'src/app/core/services/app-insights.service';
import { ToastService } from 'src/app/shared/utils/toast.service';
import { AVAILABLE_LANGUAGES } from 'src/app/core/constants/language.constants';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import { Observable } from 'rxjs';
import { ShellService } from '../../shared/shells/shell.service';
import { ShellType } from '../../core/domain/shell';
import { DesktopShellComponent } from '../../shared/shells/desktop-shell/desktop-shell.component';
import { MobileShellComponent } from '../../shared/shells/mobile-shell/mobile-shell.component';
import { MessageUnreadService } from '../messages/shared/message-unread.service';
import { isMessagesContentEnabled } from '../messages/shared/messages-feature.util';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { isNativeApp } from '../../core/auth/native-auth';
import { silent5xxContext } from '../../core/interceptors/http-context.tokens';

/**
 * DI token for the Capacitor `App` plugin. Injecting the plugin (instead of
 * calling the static proxy directly) keeps the warm-resume badge refresh
 * unit-testable: the real plugin proxy is provided in production while tests
 * substitute a mock (Capacitor plugin proxies can't be spied directly). Mirrors
 * the `PUSH_NOTIFICATIONS` token in native-push.service.ts.
 */
export const APP_PLUGIN = new InjectionToken<typeof App>(
  'APP_PLUGIN',
  { providedIn: 'root', factory: () => App },
);

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatNativeDateModule,
    DesktopShellComponent,
    MobileShellComponent
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss']
})
export class MainLayoutComponent implements OnInit, OnDestroy {

  private userService = inject(UserService);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private appInsightsService = inject(AppInsightsService);
  private toastService = inject(ToastService);
  private dateAdapter = inject(DateAdapter<Date>);
  private shellService = inject(ShellService);
  private unreadService = inject(MessageUnreadService);
  private zone = inject(NgZone);
  private app = inject(APP_PLUGIN);
  private destroyRef = inject(DestroyRef);

  /** Capacitor `appStateChange` handle, removed on destroy. */
  private appStateListener?: PluginListenerHandle;

  /** Bound `visibilitychange` handler (web foreground refresh), removed on destroy. */
  private readonly onVisibilityChange = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      this.zone.run(() => {
        // Pick up server-side changes (e.g. a newly-enabled `pulse` feature) on
        // every foreground activation without a full logout (spec: post-login
        // profile refresh). Not tied to any route navigation.
        this.refreshProfileOnActivation();
        if (isMessagesContentEnabled(this.userService)) {
          this.unreadService.refresh();
        }
      });
    }
  };

  /** Guard so the fallback poll is started at most once. */
  private pollingStarted = false;

  /**
   * Bounded number of automatic startup profile-fetch recovery attempts kicked
   * off from the component after the UserService's own transient retries are
   * exhausted. Prevents an infinite reload loop when the network stays down
   * while still surviving a longer connectivity blip without the unicorn.
   */
  private static readonly MAX_PROFILE_RECOVERY_ATTEMPTS = 2;

  /** How many startup profile recovery attempts have been made so far. */
  private profileRecoveryAttempts = 0;

  /**
   * Observable that emits the current shell type ('desktop' or 'mobile').
   * Used in template for conditional shell rendering.
   */
  shell$: Observable<ShellType> = this.shellService.shell$;

  constructor() {}

  ngOnInit() {
    // Wait until the user is authenticated
    this.authService.isAuthenticated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isAuth => {
      if (isAuth) {
        // Fetch the profile (this triggers userService.user$ to emit)
        this.loadStartupProfile();

        // When the user object updates, fetch your master data
        this.userService.user$
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(user => {
          if (user && user.user_metadata?.onboarding_complete) {
            // Get the language from the user profile (or default to 'en')
            const userLang = (user.user_metadata?.preferences?.language || 'en').toLowerCase();
            const supportedLangs = AVAILABLE_LANGUAGES.map(lang => lang.code);
            // If the user's language is not supported, fallback to 'en'
            const finalLang = supportedLangs.includes(userLang) ? userLang : 'en';
            this.appInsightsService.trackTrace('MainLayoutComponent::LanguageDetection', {
              component: 'MainLayoutComponent',
              message: 'User language detected',
              userLanguage: userLang,
              supportedLanguages: supportedLangs,
              language: finalLang
            });

            // Set the default language for the translate service
            this.translate.setDefaultLang(finalLang);
            this.translate.use(finalLang);

            // Set the locale for Angular Material's DateAdapter.
            this.dateAdapter.setLocale(finalLang);

            // Populate the nav unread-count badge once the authenticated user
            // has the Messages content enabled (real flag or local-dev override).
            // Resilient: MessageUnreadService swallows fetch errors → no badge.
            if (isMessagesContentEnabled(this.userService)) {
              this.unreadService.refresh();
              // Low-cadence, visibility-aware safety net for missed events
              // (e.g. a push that never arrived, or a web tab left open). The
              // badge is primarily kept fresh by events, not this timer.
              if (!this.pollingStarted) {
                this.pollingStarted = true;
                this.unreadService.startPolling();
              }
            }
          }
        });
      }
    });

    // Keep the Message Center unread badge accurate on a warm resume.
    void this.registerForegroundBadgeRefresh();

    // Web foreground: mirror the native `appStateChange` refresh so returning to
    // a browser tab reconciles the badge (native shells also emit this event).
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  ngOnDestroy(): void {
    void this.appStateListener?.remove();
    this.unreadService.stopPolling();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  /**
   * Fetch the authenticated user's profile at startup, surviving transient
   * network/token-timeout failures instead of crashing into the generic
   * `/global-error` unicorn.
   *
   * The subscribe supplies an ERROR callback on purpose: without it, a failure
   * that escaped `UserService.getProfile()`'s own bounded transient retries would
   * become an unhandled RxJS error → Angular `GlobalErrorHandler` →
   * `/global-error`. Instead we:
   * - For a transient error (Auth0 token timeout or 0/502/503/504): show a
   *   recoverable "connection is slow" toast and re-invoke the fetch a bounded
   *   number of times, so a longer connectivity blip self-heals silently.
   * - For a non-transient error (401/403/404/…): the existing HTTP interceptors
   *   have ALREADY applied their redirect/logout behavior; we simply avoid
   *   re-navigating to the unicorn and record telemetry.
   */
  private loadStartupProfile(): void {
    // Boot-time profile fetch: mark it SILENT_5XX so a transient cold-start 5xx
    // doesn't ALSO trigger the redundant red error toast + /page-error-500
    // redirect. This method already has its own graceful recovery below (a
    // "connection slow" WARNING toast + bounded re-fetch), so the interceptor's
    // intrusive UX on top of that was pure morning cold-start noise.
    this.userService.getProfile(false, silent5xxContext())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Success resets the recovery budget for any future re-auth cycle.
          this.profileRecoveryAttempts = 0;
        },
        error: (error: unknown) => {
          if (isTransientProfileError(error) &&
              this.profileRecoveryAttempts < MainLayoutComponent.MAX_PROFILE_RECOVERY_ATTEMPTS) {
            this.profileRecoveryAttempts++;
            this.appInsightsService.trackTrace('MainLayoutComponent::ProfileRecovery', {
              component: 'MainLayoutComponent',
              message: 'Transient profile-fetch failure at startup, retrying',
              attempt: this.profileRecoveryAttempts,
              maxAttempts: MainLayoutComponent.MAX_PROFILE_RECOVERY_ATTEMPTS
            });
            this.toastService.showToast(
              this.translate.instant('CONTROL_MESSAGES.ERROR.CONNECTION_SLOW'),
              'warning'
            );
            this.loadStartupProfile();
            return;
          }

          // Either non-transient (already handled by the interceptors) or the
          // recovery budget is exhausted. Do NOT route to /global-error for a
          // mere connectivity/timeout — just record it.
          this.appInsightsService.trackTrace('MainLayoutComponent::ProfileLoadFailed', {
            component: 'MainLayoutComponent',
            message: 'Startup profile fetch failed without navigating to global-error',
            transient: isTransientProfileError(error)
          });
        }
      });
  }

  /**
   * Refresh the unread-message badge whenever the native app returns to the
   * foreground.
   *
   * A push that arrives while the app is backgrounded or killed is handled by
   * the OS notification tray and runs NO app code, so the in-app badge would
   * otherwise stay stale until the user opened a message. On the native shell we
   * listen for Capacitor `appStateChange` and re-fetch the unread count each
   * time the app becomes active again — gated by the same authed + messages-
   * enabled predicate as the initial load (main-layout only renders post-auth;
   * `isMessagesContentEnabled` returns false when the profile/flag is absent),
   * and re-entered into Angular's zone because Capacitor callbacks fire outside
   * it (mirrors the NativeAuthService `appUrlOpen` handler). Spec 033.
   */
  private async registerForegroundBadgeRefresh(): Promise<void> {
    if (!isNativeApp()) {
      return;
    }
    this.appStateListener = await this.app.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        return;
      }
      this.zone.run(() => {
        // Refresh the profile on every native resume so a feature enabled
        // server-side while the app was backgrounded (e.g. `pulse`) becomes
        // visible without requiring a full logout (spec: post-login profile
        // refresh). Deliberately here — NOT in featureMatchGuard — so it does
        // not add an HTTP call per route navigation.
        this.refreshProfileOnActivation();
        if (isMessagesContentEnabled(this.userService)) {
          this.unreadService.refresh();
        }
      });
    });
  }

  /**
   * Force a single server profile refresh on app (re-)activation so newly
   * enabled server-side features surface without a logout. Errors are swallowed
   * (best-effort, observability only): a failed refresh must never disrupt the
   * active session, and the cached profile stays in effect. Not called per
   * route navigation.
   */
  private refreshProfileOnActivation(): void {
    this.userService.getProfile(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => undefined,
        error: () => undefined,
      });
  }
}